// CollectionControls.jsx - Filter, search, and sort controls for Collection
import React from "react";
import { ARROW_UP_ICON, ARROW_DOWN_ICON } from "../../constants/icons";

/**
 * Collection controls component with filter mode, search, feature filter, and sort
 */
function CollectionControls({
  // Visibility
  showEditControls,
  // Name search
  collectionSearchQuery,
  onSearchQueryChange,
  // Feature search
  featureDropdownRef,
  featureSearchQuery,
  onFeatureSearchChange,
  showFeatureDropdown,
  featureSearchResults,
  onAddFeature,
  // Selected features
  selectedFeatures,
  onRemoveFeature,
  // Sort
  sortBy,
  onSortByChange,
  sortOrder,
  onToggleSortOrder,
  // To Be Played pill
  showToBePlayedOnly,
  onToggleToBePlayedOnly,
}) {
  return (
    <div className={`combined-controls ${showEditControls ? "show" : "hide"}`}>
      <div
        className="combined-controls-group"
        style={{
          display: "flex",
          gap: "1rem",
          alignItems: "center",
          justifyContent: "center",
          padding: "1rem",
          backgroundColor: "var(--bg-secondary, #f5f5f5)",
          borderRadius: "8px",
          marginBottom: "1rem",
          flexWrap: "wrap",
        }}
      >

        {/* Name Search */}
        <input
          type="text"
          value={collectionSearchQuery}
          onChange={(e) => onSearchQueryChange(e.target.value)}
          placeholder="Filter by name..."
          className="profile-input"
          aria-label="Search your collection by name"
          style={{ flex: 1, minWidth: "200px" }}
        />

        {/* Feature Search */}
        <div
          className="feature-search-container"
          style={{ position: "relative", flex: 1, minWidth: "200px" }}
          ref={featureDropdownRef}
        >
          <input
            type="text"
            value={featureSearchQuery}
            onChange={(e) => onFeatureSearchChange(e.target.value)}
            placeholder="Search by mechanic, category, or theme..."
            className="profile-input"
            aria-label="Search by feature"
          />
          {showFeatureDropdown && featureSearchResults.length > 0 && (
            <div
              className="feature-search-dropdown"
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                backgroundColor: "var(--bg-primary, white)",
                border: "1px solid var(--border-color, #ddd)",
                borderRadius: "4px",
                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                maxHeight: "300px",
                overflowY: "auto",
                zIndex: 1000,
              }}
            >
              {featureSearchResults.map((feature, idx) => (
                <div
                  key={`${feature.type}-${feature.name}-${idx}`}
                  onClick={() => onAddFeature(feature.type, feature.name)}
                  style={{
                    padding: "0.75rem",
                    cursor: "pointer",
                    borderBottom: "1px solid var(--border-color, #eee)",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    backgroundColor: "transparent",
                    color: "var(--text-primary, #333)",
                    border: "none",
                    width: "100%",
                    textAlign: "left",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "var(--bg-tertiary, #f5f5f5)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <span style={{ flex: 1 }}>{feature.name}</span>
                  <span
                    style={{
                      fontSize: "0.75rem",
                      color: "var(--text-secondary, #666)",
                    }}
                  >
                    {feature.type}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* To Be Played Toggle */}
        <div
          className="to-be-played-pill"
          style={{ display: "flex", alignItems: "center" }}
        >
          <button
            onClick={onToggleToBePlayedOnly}
            style={{
              padding: "0.25rem 0.6rem",
              border: "1px solid var(--pista-green-primary, #2E7D32)",
              borderRadius: "12px",
              backgroundColor: showToBePlayedOnly
                ? "var(--pista-green-primary, #2E7D32)"
                : "var(--bg-secondary, #1e1e1e)",
              color: showToBePlayedOnly
                ? "var(--pista-button-text, white)"
                : "var(--pista-green-primary, #2E7D32)",
              fontSize: "0.75rem",
              fontWeight: "400",
              cursor: "pointer",
              transition: "all 0.2s ease",
              whiteSpace: "nowrap",
            }}
          >
            To Be Played
          </button>
        </div>

        {/* Selected Feature Pills */}
        {selectedFeatures.length > 0 && (
          <div
            className="selected-features"
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "0.5rem",
              marginTop: "0.5rem",
            }}
          >
            {selectedFeatures.map((feature, idx) => (
              <div
                key={`${feature.type}-${feature.value}-${idx}`}
                className="feature-pill"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.25rem",
                  padding: "0.25rem 0.5rem",
                  backgroundColor: "var(--bg-secondary, #e3f2fd)",
                  borderRadius: "16px",
                  fontSize: "0.85rem",
                  color: "var(--text-primary, #333)",
                  border: "1px solid var(--border-color, #ddd)",
                }}
              >
                <span>
                  {feature.type}: {feature.value}
                </span>
                <button
                  onClick={() => onRemoveFeature(feature.type, feature.value)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "0 2px",
                    fontSize: "0.75rem",
                    color: "var(--text-secondary, #666)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "var(--text-primary, #333)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "var(--text-secondary, #666)";
                  }}
                  aria-label={`Remove ${feature.value}`}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Sort Controls */}
        <div
          className="sort-group"
          style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
        >
          <select
            value={sortBy}
            onChange={(e) => onSortByChange(e.target.value)}
            className="profile-input sort-select"
            aria-label="Sort collection by"
            style={{
              padding: "0.5rem",
              border: "1px solid var(--border-color, #ddd)",
              borderRadius: "4px",
              fontSize: "0.875rem",
              backgroundColor: "var(--bg-primary, white)",
              color: "var(--text-primary, #333)",
            }}
          >
            <option value="year_published">Year Published</option>
            <option value="name">Name</option>
            <option value="preference">Preference</option>
            <option value="last_played">Last Played</option>
          </select>
          <button
            onClick={onToggleSortOrder}
            className="icon-button sort-order-button"
            aria-label={`Sort ${sortOrder === "desc" ? "ascending" : "descending"}`}
            title={`Sort ${sortOrder === "desc" ? "ascending" : "descending"}`}
          >
            {sortOrder === "desc" ? ARROW_DOWN_ICON : ARROW_UP_ICON}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CollectionControls;
